import { MedChronAssetRouteSegments, MedChronAssetTabs } from '@/medical-chronology/asset/constants'
import { MedSummaryAssetRouteSegments, MedSummaryAssetTabs } from '@/medical-billing-summaries/asset/constants'
import { TimelineAssetRouteSegments, TimelineAssetTabs } from '@/timeline/asset/constants'

export const accountPaths = {
  usage: '/dashboard/usage',
} as const

export const kRouteSegments = {
  chat: 'chat',
  chats: 'chats',
  assistant: 'assistant',
  agentV2: 'agent',
  chronology: 'medical-chronology',
  timeline: 'timeline',
  billing: 'billing-summaries',
  userFiles: 'user-files',
  referenceViewer: 'reference-viewer',
  drafts: 'drafts',
  linkOutlook: 'link-outlook',
} as const

/** Web page where a signed-in user links the Microsoft account they use in Outlook. */
export const kOutlookLinkPath = `/dashboard/${kRouteSegments.linkOutlook}`

/**
 * Centralized path builders and route patterns for matter-first routing.
 *
 * All path builders require a valid matterId. Callers should ensure they have
 * a matter context before building paths.
 *
 * Usage:
 * - Use `matterPaths.*` for building navigation URLs
 * - Use `routePatterns.*` for route matching with useMatch()
 *
 * @example
 * matterPaths.overview('123')                        // '/dashboard/m/123'
 * matterPaths.chat.feature('123', 'research')        // '/dashboard/m/123/${kRouteSegments.chat}/research'
 * matterPaths.chronology.index('123')                // '/dashboard/m/123/${kRouteSegments.chronology}'
 */
export const matterPaths = {
  /** Matter overview path (root of matter context) */
  overview: (matterId: string) => `/dashboard/m/${matterId}`,
  chats: (matterId: string) => `/dashboard/m/${matterId}/${kRouteSegments.chats}`,
  /** Matter settings path */
  settings: (matterId: string) => `/dashboard/m/${matterId}/settings`,
  /** Drafts destination path (feature-flagged on DRAFTS_DESTINATION) */
  drafts: (matterId: string) => `/dashboard/m/${matterId}/${kRouteSegments.drafts}`,
  tables: (matterId: string) => `/dashboard/m/${matterId}/tables`,
  createTable: (matterId: string) => `/dashboard/m/${matterId}/tables/create`,
  table: (matterId: string, tableId: string) => `/dashboard/m/${matterId}/tables/${tableId}`,

  /** Chat feature paths */
  chat: {
    /** Chat feature index (e.g., research, assistant, caselaw) */
    feature: (matterId: string, feature: string) => `/dashboard/m/${matterId}/${kRouteSegments.chat}/${feature}`,
    /** Specific conversation within a chat feature */
    conversation: (matterId: string, feature: string, chatId: string) => `/dashboard/m/${matterId}/${kRouteSegments.chat}/${feature}/${chatId}`,
  },

  /** Medical Chronology paths (the matter's living chronology) */
  chronology: {
    /** The living chronology (defaults to the All Events tab) */
    index: (matterId: string) => `/dashboard/m/${matterId}/${kRouteSegments.chronology}`,
    /** Specific tab within the living chronology */
    tab: (matterId: string, tab: MedChronAssetTabs) => `/dashboard/m/${matterId}/${kRouteSegments.chronology}/${tab}`,
    /** Add Files page (stage uploads/matter files, then generate or update) */
    addFiles: (matterId: string) => `/dashboard/m/${matterId}/${kRouteSegments.chronology}/${MedChronAssetRouteSegments.ADD_FILES}`,
  },

  /** Timeline (Single Matter) paths — the matter's living timeline asset */
  timeline: {
    /** The living timeline (defaults to the All Events tab) */
    index: (matterId: string) => `/dashboard/m/${matterId}/${kRouteSegments.timeline}`,
    /** Specific tab within the living timeline */
    tab: (matterId: string, tab: TimelineAssetTabs) => `/dashboard/m/${matterId}/${kRouteSegments.timeline}/${tab}`,
    /** Add Files page (stage uploads/matter files, then generate or update) */
    addFiles: (matterId: string) => `/dashboard/m/${matterId}/${kRouteSegments.timeline}/${TimelineAssetRouteSegments.ADD_FILES}`,
  },

  /** Medical Billing Summary paths */
  billing: {
    /** The billing-summaries feature root: the matter's living billing summary */
    index: (matterId: string) => `/dashboard/m/${matterId}/${kRouteSegments.billing}`,
    /** Specific tab within the living billing summary */
    assetTab: (matterId: string, tab: MedSummaryAssetTabs) => `/dashboard/m/${matterId}/${kRouteSegments.billing}/${tab}`,
    /** Add Files page (stage uploads/matter files, then generate or update the living summary) */
    assetAddFiles: (matterId: string) => `/dashboard/m/${matterId}/${kRouteSegments.billing}/${MedSummaryAssetRouteSegments.ADD_FILES}`,
  },

  /** User Files paths */
  userFiles: {
    /** User files root for a matter */
    root: (matterId: string) => `/dashboard/m/${matterId}/${kRouteSegments.userFiles}`,
    /** Specific folder within user files */
    folder: (matterId: string, folderId: string) => `/dashboard/m/${matterId}/${kRouteSegments.userFiles}/${folderId}`,
  },

  /** Reference Viewer paths */
  referenceViewer: {
    artifact: (matterId: string, conversationId: string, versionId: string) =>
      `/dashboard/m/${matterId}/reference-viewer/artifact/${conversationId}/${versionId}`,
    agency: (matterId: string, conversationId: string, referenceId: string) =>
      `/dashboard/m/${matterId}/reference-viewer/agency/${conversationId}/${referenceId}`,
    caselaw: (matterId: string | null, idType: string, id: string) =>
      matterId
        ? `/dashboard/m/${matterId}/${kRouteSegments.referenceViewer}/caselaw/${idType}/${id}`
        : `/dashboard/${kRouteSegments.referenceViewer}/caselaw/${idType}/${id}`,
    lrr: (matterId: string | null, s3Path: string) =>
      matterId ? `/dashboard/m/${matterId}/${kRouteSegments.referenceViewer}/lrr/${s3Path}` : `/dashboard/${kRouteSegments.referenceViewer}/lrr/${s3Path}`,
    userFile: (matterId: string, userFileId: string) => `/dashboard/m/${matterId}/${kRouteSegments.referenceViewer}/user-file/${userFileId}`,
  },
} as const

// =============================================================================
// Route Patterns (for useMatch)
// =============================================================================

/**
 * Route patterns for matching URLs with useMatch().
 *
 * @example
 * const isChronologyRoute = useMatch(routePatterns.chronology.matterFirst)
 * const isAssistantRoute = useMatch(routePatterns.chat.matterFirstFeature)
 */
export const routePatterns = {
  /** Matter overview route — matches /dashboard/m/:matterId with no sub-path */
  matterOverview: `/dashboard/m/:matterId`,

  /**
   * Drafts destination route — matches only the list itself, with no `/*` and no sub-path.
   *
   * Opening a draft leaves the destination for its source chat, so anything below this path would
   * keep the sidebar's Drafts item selected while the user is somewhere else.
   */
  drafts: `/dashboard/m/:matterId/${kRouteSegments.drafts}`,

  /** Medical Chronology route patterns */
  chronology: {
    /** Matter-first URL pattern for useMatch */
    matterFirst: `/dashboard/m/:matterId/${kRouteSegments.chronology}/*`,
  },

  /** Timeline (Single Matter) route patterns */
  timeline: {
    /** Matter-first URL pattern for useMatch */
    matterFirst: `/dashboard/m/:matterId/${kRouteSegments.timeline}/*`,
  },

  /** Medical Billing Summaries route patterns */
  billingSummaries: {
    /** Matter-first URL pattern for useMatch */
    matterFirst: `/dashboard/m/:matterId/${kRouteSegments.billing}/*`,
  },

  /** Chat feature route patterns */
  chat: {
    /** Matter-first feature-specific pattern for useMatch */
    matterFirstFeature: `/dashboard/m/:matterId/${kRouteSegments.chat}/:chatFeature/*`,
  },

  /** User Files route patterns */
  userFiles: {
    /** Matter-first URL pattern for useMatch */
    matterFirst: `/dashboard/m/:matterId/${kRouteSegments.userFiles}/*`,
  },
} as const
